import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { unwrapList } from '@/utils/apiEnvelope';

// Where each Slack alert family posts. One row per key on the backend; a key
// with no row (or no URL) falls back to the service's env var, so the screen
// is an override, never a requirement. Legacy `/v1` convention: the actor
// rides in the body.
const BASE = '/v1/fleet/setting/notification-channels';

export type NotificationChannelSource = 'setting' | 'env' | 'none' | 'muted';

export interface NotificationChannel {
  key: string;
  label: string;
  description: string;
  channelName: string | null;
  /** Host + last characters only — the whole URL never leaves the server. */
  webhookURLMasked: string | null;
  source: NotificationChannelSource;
  hasEnvFallback: boolean;
  isActive: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface UpdateNotificationChannelRequest {
  channelName?: string | null;
  /** Omit to keep the stored URL; '' or null clears it (env applies again). */
  webhookURL?: string | null;
  isActive: boolean;
  updatedBy: string;
}

export const SLACK_WEBHOOK_PREFIX = 'https://hooks.slack.com/services/';

export const SOURCE_LABELS: Record<NotificationChannelSource, string> = {
  setting: 'Dari setting',
  env: 'Dari env server',
  none: 'Tidak terkirim',
  muted: 'Dibisukan',
};

export const getNotificationChannels = async (): Promise<
  NotificationChannel[]
> => {
  try {
    const response = await Axios.get(BASE);
    return unwrapList<NotificationChannel>(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const updateNotificationChannel = async (
  key: string,
  data: UpdateNotificationChannelRequest
): Promise<void> => {
  try {
    await Axios.put(`${BASE}/${key}`, data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const testNotificationChannel = async (
  key: string,
  requestedBy: string
): Promise<{ source: NotificationChannelSource }> => {
  try {
    const response = await Axios.post(`${BASE}/${key}/test`, { requestedBy });
    return response.data?.data ?? response.data;
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
